// BC account detail (F3): everything the cache holds for one ad account —
// header (name/status/ids/linked clients), the raw info/balance_info JSONB
// grids, balance + spend cards, cached recent ads — plus the LIVE campaigns
// table and the v1 write actions (rename, BC budget, campaign ENABLE/DISABLE
// toggles), all confirm-gated like the client-disable flow.
//
// SECURITY: the info/balance_info grids render TikTok RESPONSE data only,
// sanitized/cached server-side — they never contain our master token. Writes
// go to the admin backend's allow-listed action endpoints (no generic TikTok
// pass-through exists); an upstream failure surfaces ONLY the backend's fixed
// sanitized message ("upstream error") via the ApiError toast. Money values
// are NUMERIC-fidelity strings rendered verbatim, never parsed into floats.
//
// CHROME: NEXOVA AdminShell + nexus primitives (StatCard, Card sections,
// DataTable, tone status chips, Modal confirm). The data hooks, write flow,
// and money fidelity are unchanged from the legacy screen.

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import { api } from "@/api/client";
import { errMessage } from "@/lib/errMessage";
import { AdminShell } from "@/components/nexus/AdminShell";
import { financeView, type FinanceViewFields } from "@/api/financeview";
import {
  AccountStatusChip,
  AdsRunningDot,
  ClientBadges,
  FinanceViewBadge,
  financeViewLine,
  StaleBanner,
  syncedLabel,
  type ClientRef,
} from "@/routes/DashboardPage";
import {
  DataTable,
  Modal,
  StatCard,
  ToneBadge,
  type Column,
} from "@/components/nexus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

// The detail body carries the SAME two finance-visibility fields as every list
// row (ADR-0054): whether any authorized identity can read this account's
// wallet/budget balance, and via which slots. Optional in the TYPE only, to
// tolerate an older backend that omits them (see @/api/financeview).
interface AccountBody extends FinanceViewFields {
  account_id: string;
  bc_id: string;
  name: string;
  display_status: string;
  status: string;
  account_type: string;
  currency: string;
  timezone: string;
  balance: string; // decimal string; "" = unknown
  // Raw JSONB passthrough of TikTok response data (null = never enriched).
  info: Record<string, unknown> | null;
  balance_info: Record<string, unknown> | null;
  synced_at: number;
}

interface SpendWindow {
  spend: string;
  impressions: number;
  clicks: number;
}

interface Activity {
  ads_running: boolean;
  running_campaigns: number;
}

interface RecentAd {
  ad_id: string;
  ad_name: string;
  campaign_name: string;
  spend: string;
  currency: string;
  synced_at: number;
}

interface Campaign {
  campaign_id: string;
  campaign_name: string;
  operation_status: string;
  secondary_status: string;
  budget: string;
  budget_mode: string;
  objective_type: string;
  modify_time: string;
}

interface AccountDetail {
  account: AccountBody;
  clients: ClientRef[];
  spend: Record<string, SpendWindow>;
  activity: Activity;
  recent_ads: RecentAd[];
  // LIVE campaign list; null + campaigns_error when the fetch failed.
  campaigns: Campaign[] | null;
  campaigns_error: boolean;
  stale: boolean;
}

// The v1 budget-mode allowlist (spec §5; CUSTOM_BUDGET deliberately excluded).
const budgetModes = ["UNLIMITED", "MONTHLY_BUDGET", "DAILY_BUDGET"] as const;
type BudgetMode = (typeof budgetModes)[number];

const maxRenameLen = 512;

// Plain unsigned decimal with at most one interior dot — mirrors the
// backend's isMoneyDecimal guard so invalid input fails fast client-side.
function isMoneyDecimal(s: string): boolean {
  return s.length > 0 && s.length <= 32 && /^\d+(\.\d+)?$/.test(s);
}

// One pending confirm-gated write at a time (same gating as client disable).
type PendingAction =
  | { kind: "rename" }
  | { kind: "budget" }
  | { kind: "campaign"; campaign: Campaign; op: "ENABLE" | "DISABLE" };

// Render one JSONB passthrough value: epoch-second *_time numbers as dates,
// booleans as yes/no, nested objects as compact JSON, everything else as-is.
function formatJsonbValue(key: string, value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "number" && key.endsWith("_time")) {
    return new Date(value * 1000).toLocaleString();
  }
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

// Key/value grid over a raw JSONB object — full detail-page fidelity: every
// field TikTok returned is shown, keys verbatim.
function JsonbGrid({ data }: { data: Record<string, unknown> | null }) {
  const entries = Object.entries(data ?? {});
  if (entries.length === 0) {
    return <p className="text-sm text-nx-text-3">Not enriched yet.</p>;
  }
  return (
    <dl className="grid grid-cols-1 gap-x-8 gap-y-1.5 sm:grid-cols-2">
      {entries.map(([k, v]) => (
        <div
          key={k}
          className="flex items-baseline justify-between gap-3 border-b border-nx-border/50 pb-1"
        >
          <dt className="font-mono text-xs text-nx-text-3">{k}</dt>
          <dd className="text-right text-sm break-all text-nx-text-2">
            {formatJsonbValue(k, v)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function AccountDetailPage() {
  const { id = "" } = useParams();

  const [detail, setDetail] = useState<AccountDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Action forms + the pending confirm dialog.
  const [renameName, setRenameName] = useState("");
  const [budgetMode, setBudgetMode] = useState<BudgetMode>("UNLIMITED");
  const [budgetAmount, setBudgetAmount] = useState("");
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<AccountDetail>(
        `/admin/dashboard/accounts/${id}`,
      );
      setDetail(res);
      setRenameName(res.account.name);
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Execute the confirmed action. On success: toast, close, reload from the
  // backend (which refreshed its cache row best-effort). On failure the toast
  // carries ONLY the sanitized ApiError message and the dialog stays open.
  async function runPending() {
    if (!pending) return;
    setBusy(true);
    try {
      if (pending.kind === "rename") {
        await api.post(`/admin/dashboard/accounts/${id}/rename`, {
          name: renameName.trim(),
        });
        toast.success("Account renamed");
      } else if (pending.kind === "budget") {
        const body: { budget_mode: string; budget?: string } = {
          budget_mode: budgetMode,
        };
        if (budgetMode !== "UNLIMITED") {
          body.budget = budgetAmount.trim();
        }
        await api.post(`/admin/dashboard/accounts/${id}/budget`, body);
        toast.success("Budget updated");
      } else {
        await api.post(
          `/admin/dashboard/accounts/${id}/campaigns/${pending.campaign.campaign_id}/status`,
          { operation_status: pending.op },
        );
        toast.success(
          pending.op === "ENABLE" ? "Campaign enabled" : "Campaign disabled",
        );
      }
      setPending(null);
      await load();
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function pendingTitle(p: PendingAction): string {
    switch (p.kind) {
      case "rename":
        return "Rename this account?";
      case "budget":
        return "Update the BC budget?";
      case "campaign":
        return `${p.op === "ENABLE" ? "Enable" : "Disable"} ${p.campaign.campaign_name}?`;
    }
  }

  function pendingDescription(p: PendingAction): string {
    switch (p.kind) {
      case "rename":
        return `The ad account is renamed to "${renameName.trim()}" at TikTok immediately.`;
      case "budget":
        return budgetMode === "UNLIMITED"
          ? "Removes the Business-Center budget cap for this account."
          : `Sets ${budgetMode} of ${budgetAmount.trim()} ${detail?.account.currency ?? ""} for this account.`;
      case "campaign":
        return p.op === "ENABLE"
          ? "The campaign may start delivering again immediately."
          : "The campaign stops delivering immediately.";
    }
  }

  const acct = detail?.account;
  const spend7 = detail?.spend["7d"];
  const spend30 = detail?.spend["30d"];
  const renameTrimmed = renameName.trim();
  const renameValid =
    renameTrimmed !== "" && renameTrimmed.length <= maxRenameLen;
  const budgetValid =
    budgetMode === "UNLIMITED" || isMoneyDecimal(budgetAmount.trim());

  const campaignColumns: Column<Campaign>[] = [
    {
      key: "campaign",
      header: "Campaign",
      cell: (c) => (
        <div className="grid">
          <span className="font-medium text-nx-text">{c.campaign_name}</span>
          <span className="font-mono text-xs text-nx-text-3">
            {c.campaign_id}
          </span>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (c) => (
        <div className="grid gap-1">
          <ToneBadge tone={c.operation_status === "ENABLE" ? "live" : "na"}>
            {c.operation_status}
          </ToneBadge>
          <span className="text-xs text-nx-text-3">{c.secondary_status}</span>
        </div>
      ),
    },
    {
      key: "budget",
      header: "Budget",
      cell: (c) => (
        <span className="font-mono text-sm text-nx-text-2 tabular-nums">
          {c.budget}
          <span className="block text-xs text-nx-text-3">{c.budget_mode}</span>
        </span>
      ),
    },
    {
      key: "objective",
      header: "Objective",
      cell: (c) => <span className="text-nx-text-2">{c.objective_type}</span>,
    },
    {
      key: "modified",
      header: "Modified",
      cell: (c) => <span className="text-nx-text-3">{c.modify_time}</span>,
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      cell: (c) =>
        c.operation_status === "ENABLE" ? (
          <Button
            variant="destructive"
            size="sm"
            disabled={busy}
            onClick={() =>
              setPending({ kind: "campaign", campaign: c, op: "DISABLE" })
            }
          >
            Disable
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() =>
              setPending({ kind: "campaign", campaign: c, op: "ENABLE" })
            }
          >
            Enable
          </Button>
        ),
    },
  ];

  const recentAdColumns: Column<RecentAd>[] = [
    {
      key: "ad",
      header: "Ad",
      cell: (ad) => (
        <div className="grid">
          <span className="font-medium text-nx-text">{ad.ad_name}</span>
          <span className="font-mono text-xs text-nx-text-3">{ad.ad_id}</span>
        </div>
      ),
    },
    {
      key: "campaign",
      header: "Campaign",
      cell: (ad) => <span className="text-nx-text-2">{ad.campaign_name}</span>,
    },
    {
      key: "spend",
      header: "Spend",
      cell: (ad) => (
        <span className="font-mono text-sm text-nx-text-2 tabular-nums">
          {ad.spend}
        </span>
      ),
    },
    {
      key: "currency",
      header: "Currency",
      cell: (ad) => <span className="text-nx-text-2">{ad.currency}</span>,
    },
  ];

  return (
    <AdminShell>
      <div className="grid gap-6">
        <div className="flex items-center justify-between">
          <Link
            to="/dashboard"
            className="text-sm text-nx-text-2 underline-offset-4 hover:text-nx-text hover:underline"
          >
            ← Back to dashboard
          </Link>
          {acct && (
            <span className="text-sm text-nx-text-3">
              {syncedLabel(acct.synced_at)}
            </span>
          )}
        </div>

        {detail?.stale && <StaleBanner />}

        {error && (
          <Alert variant="destructive">
            <AlertTitle>Could not load account</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading ? (
          <div className="grid gap-2">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : detail && acct ? (
          <>
            {/* Header */}
            <Card>
              <CardContent className="grid gap-1.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-lg font-medium text-nx-text">
                    {acct.name}
                  </span>
                  <AccountStatusChip displayStatus={acct.display_status} />
                  <ClientBadges clients={detail.clients} />
                </div>
                <div className="font-mono text-sm text-nx-text-3">
                  Account {acct.account_id} · BC {acct.bc_id}
                </div>
                <div className="text-sm text-nx-text-3">
                  {acct.status} · {acct.account_type} · {acct.currency} ·{" "}
                  {acct.timezone}
                </div>
                <div className="flex items-center gap-2 text-sm text-nx-text-2">
                  <AdsRunningDot running={detail.activity.ads_running} />
                  {detail.activity.ads_running
                    ? `${detail.activity.running_campaigns} campaign(s) delivering`
                    : "No ads running"}
                </div>
              </CardContent>
            </Card>

            {/* Balance + spend cards */}
            <div className="grid gap-4 lg:grid-cols-3">
              <StatCard
                label="Balance"
                value={
                  acct.balance === "" ? "—" : `${acct.balance} ${acct.currency}`
                }
              />
              <StatCard
                label="Spend — last 7 days"
                value={spend7?.spend ?? "0"}
                meta={`${acct.currency} · ${spend7?.impressions ?? 0} impressions · ${spend7?.clicks ?? 0} clicks`}
              />
              <StatCard
                label="Spend — last 30 days"
                value={spend30?.spend ?? "0"}
                meta={`${acct.currency} · ${spend30?.impressions ?? 0} impressions · ${spend30?.clicks ?? 0} clicks`}
              />
            </div>

            {/* Can ANY authorized identity read this account's wallet/budget
                balance, and through which slot(s)? Rendered only when the
                backend reports it — an older deploy omits the fields and must
                not be shown as "no financial view". */}
            {financeView(acct).known && (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-xs font-medium tracking-wide text-nx-text-3 uppercase">
                  Financial view
                </span>
                <FinanceViewBadge account={acct} />
                <span className="text-nx-text-2">{financeViewLine(acct)}</span>
              </div>
            )}

            {/* Actions: rename + BC budget (both confirm-gated). */}
            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Rename account</CardTitle>
                  <CardDescription>
                    Renames the ad account at TikTok (max 512 characters).
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex items-center gap-2">
                  <Input
                    aria-label="New name"
                    value={renameName}
                    onChange={(e) => setRenameName(e.target.value)}
                  />
                  <Button
                    disabled={busy || !renameValid}
                    onClick={() => setPending({ kind: "rename" })}
                  >
                    Rename
                  </Button>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>BC budget</CardTitle>
                  <CardDescription>
                    Business-Center budget for this account (UNLIMITED needs
                    no amount).
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-wrap items-center gap-2">
                  <select
                    aria-label="Budget mode"
                    className="h-9 rounded-lg border border-nx-border bg-nx-bg-2 px-2.5 text-sm text-nx-text outline-none focus-visible:border-nx-brand focus-visible:ring-3 focus-visible:ring-nx-brand/20"
                    value={budgetMode}
                    onChange={(e) => setBudgetMode(e.target.value as BudgetMode)}
                  >
                    {budgetModes.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                  {budgetMode !== "UNLIMITED" && (
                    <Input
                      aria-label="Budget amount"
                      inputMode="decimal"
                      placeholder={`Amount (${acct.currency})`}
                      className="w-40"
                      value={budgetAmount}
                      onChange={(e) => setBudgetAmount(e.target.value)}
                    />
                  )}
                  <Button
                    disabled={busy || !budgetValid}
                    onClick={() => setPending({ kind: "budget" })}
                  >
                    Update budget
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* LIVE campaigns with confirm-gated ENABLE/DISABLE toggles. */}
            <Card>
              <CardHeader>
                <CardTitle>Campaigns</CardTitle>
                <CardDescription>
                  Fetched live from TikTok — not cached.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3">
                {detail.campaigns_error ? (
                  <Alert variant="destructive">
                    <AlertTitle>Could not load live campaigns</AlertTitle>
                    <AlertDescription>
                      TikTok did not answer; the cached data above is
                      unaffected. Reload to retry.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <DataTable
                    columns={campaignColumns}
                    rows={detail.campaigns ?? []}
                    rowKey={(c) => c.campaign_id}
                    empty="No campaigns on this account."
                  />
                )}
              </CardContent>
            </Card>

            {/* Cached recent ads for this account. */}
            <Card>
              <CardHeader>
                <CardTitle>Recent ads</CardTitle>
                <CardDescription>
                  Top ads by spend for this account (cached).
                </CardDescription>
              </CardHeader>
              <CardContent>
                <DataTable
                  columns={recentAdColumns}
                  rows={detail.recent_ads}
                  rowKey={(ad) => ad.ad_id}
                  empty="No recent ads cached for this account."
                />
              </CardContent>
            </Card>

            {/* Raw JSONB passthrough grids (TikTok response data only). */}
            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Account info</CardTitle>
                  <CardDescription>
                    Full advertiser profile as returned by TikTok.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <JsonbGrid data={acct.info} />
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Balance info</CardTitle>
                  <CardDescription>
                    Balance/budget snapshot as returned by TikTok.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <JsonbGrid data={acct.balance_info} />
                </CardContent>
              </Card>
            </div>
          </>
        ) : null}
      </div>

      {/* Shared confirm dialog for every v1 write. */}
      <Modal
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
        title={pending ? pendingTitle(pending) : ""}
        description={pending ? pendingDescription(pending) : ""}
        footer={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => setPending(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant={
                pending?.kind === "campaign" && pending.op === "DISABLE"
                  ? "destructive"
                  : "default"
              }
              disabled={busy}
              onClick={runPending}
            >
              Confirm
            </Button>
          </>
        }
      />
    </AdminShell>
  );
}
